import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm, SubmitHandler } from 'react-hook-form';
import styles from './Step5.module.scss';
import global from '../../../styles/index.module.scss';
import { GlobalData, LocationState } from '../../../types';
import { Layout } from '../../../components/common/Layout/Layout';
import { StepHeader } from '../../../components/common/StepHeader/StepHeader';
import { ChangeButtonLayout } from '../../../components/common/ChangeButtonLayout/ChangeButtonLayout';
import { redirectTo404 } from '../../../utils/redirectTo404';
import { prefectures, materialOptions } from '../constants';

interface Step5Props {
  data: GlobalData[];
  firstCollectionHandle: string | null;
  secondCollectionHandle: string | null;
  productHandle: string | null;
}

interface FormValues {
  email: string;
  bland: string;
  request: string;
  prefecture: string;
  area: string;
  material?: string;
}

export const Step5: React.FC<Step5Props> = ({
  data,
  firstCollectionHandle,
  secondCollectionHandle,
  productHandle,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    images: initialImages = {},
    email: initialEmail = '',
    bland: initialBland = '',
    request: initialRequest = '',
    prefecture: initialPrefecture = '',
    area: initialArea = '',
    material: initialMaterial
  } = location.state as LocationState || {};

  const { register, handleSubmit, formState: { errors, isValid } } = useForm<FormValues>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      email: initialEmail,
      bland: initialBland,
      request: initialRequest,
      prefecture: initialPrefecture,
      area: initialArea,
      material: initialMaterial
    },
  });

  if (!firstCollectionHandle || !secondCollectionHandle || !productHandle) {
    redirectTo404();
    return null;
  }

  const collectionData = data.find((d) => d.first_collection.handle === firstCollectionHandle);


  const secondCollectionData = collectionData?.second_collections.find(
    (sc) => sc.second_collection.handle === secondCollectionHandle
  );

  const productData = secondCollectionData?.products.find(
    (p) => p.product.handle === productHandle
  );

  const onSubmit: SubmitHandler<FormValues> = data => {
    // 現在のURLパラメータを取得
    const currentParams = new URLSearchParams(window.location.search);
    currentParams.set('step', '6');
    currentParams.set('firstcollectionhandle', firstCollectionHandle || '');
    currentParams.set('secondcollectionhandle', secondCollectionHandle || '');
    currentParams.set('producthandle', productHandle || '');
    const linkPath = `/?${currentParams.toString()}`;
    
    navigate(linkPath, { 
      state: { 
        images: initialImages, 
        email: data.email, 
        bland: data.bland, 
        request: data.request, 
        prefecture: data.prefecture, 
        area: data.area, 
        material: data.material 
      } 
    });
  };

  const shouldDisplayMaterialRadio = collectionData && ["メンズ-シューズ", "レディース-シューズ", "メンズ-スニーカー", "レディース-スニーカー", "バッグ-リュック-財布", "ベルトや革小物など"].includes(collectionData.first_collection.handle);

  return (
    <div className={styles.step5}>
      <Layout>
        <ChangeButtonLayout
          stepNumber={4}
          firstCollectionHandle={firstCollectionHandle}
          firstCollectionTitle={collectionData?.first_collection.title}
          secondCollectionHandle={secondCollectionHandle}
          secondCollectionTitle={secondCollectionData?.second_collection.title}
          productHandle={productHandle}
          productTitle={productData?.product.title}
        />
      </Layout>
      <Layout>
        <StepHeader stepNumber={5} title="修理相談内容を記入してください" firstcollectionhandle={firstCollectionHandle} />
      </Layout>
      <form onSubmit={(event) => void handleSubmit(onSubmit)(event)} className={styles.formSection} noValidate>
        <div className={styles.formGroup}>
          <Layout>
            <div className={styles.formWrapper}>
              <label htmlFor="email" className={styles.formTitle}>
                メールアドレス
              </label>
              <input
                type="text"
                id="email"
                placeholder="回答のご連絡に使います。お間違えのないようご記入ください"
                className={styles.formInput}
                {...register('email', { required: 'メールアドレスは必須です', pattern: { value: /^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/, message: '有効なメールアドレスを入力してください' } })}
              />
              {errors.email && <p className={styles.errorText}>{errors.email.message}</p>}
            </div>
          </Layout>
        </div>
        <div className={styles.formGroup}>
          <Layout>
            <div className={styles.formWrapper}>
              <label htmlFor="bland" className={styles.formTitle}>
                ブランド名・メーカー名
              </label>
              <span className={styles.formSubTitle}>
                スーツケースはメーカー名と型番をご記入ください。
              </span>
              <input
                type="text"
                id="bland"
                placeholder="わからない場合は「不明」とご記入ください"
                className={styles.formInput}
                {...register('bland', { required: 'ブランド名・メーカー名は必須です' })}
              />
              {errors.bland && <p className={styles.errorText}>{errors.bland.message}</p>}
            </div>
          </Layout>
        </div>
        {shouldDisplayMaterialRadio && (
          <div className={styles.formGroup}>
            <Layout>
              <div className={styles.formWrapper}>
                <label className={styles.formTitle}>
                  素材を教えてください
                </label>
                <div className={styles.formRadioGroup}>
                  {materialOptions.map(option => (
                    <label key={option} className={styles.formRadioLabel}>
                      <input
                        type="radio"
                        value={option}
                        {...register('material', { required: '素材は必須です' })}
                      />
                      {option}
                    </label>
                  ))}
                </div>
                {errors.material && <p className={styles.errorText}>{errors.material.message}</p>}
              </div>
            </Layout>
          </div>
        )}
        <div className={styles.formGroup}>
          <Layout>
            <div className={styles.formWrapper}>
              <label htmlFor="request" className={styles.formTitle}>
                ご相談内容をご記入ください
              </label>
              <span className={styles.formSubTitle}>
                なるべく具体的にご記入ください。
              </span>
              <span className={styles.formSubTitle}>
                同じアイテムでその他の部分も修理相談したい場合は、ここに記入してください。
              </span>
              <textarea
                id="request"
                placeholder="例） 両足のつま先部分の裏側が2cm程度剥がれてしまいました。つま先もですが、靴底が減っているので合わせて修理したいです。また靴の表面が色褪せてキズが多いのですが修理できますか？"
                className={styles.formTextarea}
                {...register('request', { required: 'ご依頼内容は必須です' })}
              />
              {errors.request && <p className={styles.errorText}>{errors.request.message}</p>}
            </div>
          </Layout>
        </div>
        <div className={styles.formGroup}>
          <Layout>
            <div className={styles.formWrapper}>
              <label htmlFor="prefecture" className={styles.formTitle}>
                お住まいの都道府県
              </label>
              <select
                id="prefecture"
                className={styles.formSelect}
                {...register('prefecture', { required: '都道府県は必須です' })}
              >
                <option value="">選択してください</option>
                {prefectures.map((prefecture) => (
                  <option key={prefecture} value={prefecture}>
                    {prefecture}
                  </option>
                ))}
              </select>
              {errors.prefecture && <p className={styles.errorText}>{errors.prefecture.message}</p>}
            </div>
          </Layout>
        </div>
        <div className={styles.formGroup}>
          <Layout>
            <div className={styles.formWrapper}>
              <label htmlFor="area" className={styles.formTitle}>
                お店をご利用いただく場合のエリア
              </label>
              <input
                type="text"
                id="area"
                placeholder="（例）新宿駅、xx線沿線、福岡・天神 、横浜駅"
                className={styles.formInput}
                {...register('area', { required: 'お店をご利用いただく場合のエリアは必須です' })}
              />
              {errors.area && <p className={styles.errorText}>{errors.area.message}</p>}
            </div>
          </Layout>
        </div>
        <div className={`${styles.buttonWrapper} ${"js-trackEvent"}`}>
          {!isValid && (
            <div className={styles.alertMessage}>
              入力項目が不足しています
            </div>
          )}
          <button
            type="submit"
            className={`${global.primaryButton} ${!isValid ? global.disabled : ''}`}
            data-ga-id="Clk_Btn_AppPage5_NextBtn"
            disabled={!isValid}
          >
            確認画面へ進む
          </button>
        </div>
      </form>
    </div>
  );
};